// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Property / Belongings Tracking
// Check-in, check-out, and QR code generation for belongings
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Drawer, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconLogout, IconPrinter, IconTags,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { notifications } from '../../lib/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { printPropertyLabelSheet } from '../../lib/printLabels';
import type { ShelterProperty } from '../../types/shelter';

const CATEGORY_OPTIONS = [
  { value: 'bag', label: 'Bag / Luggage' },
  { value: 'documents', label: 'Documents' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'valuables', label: 'Valuables' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  bag: 'blue', documents: 'teal', electronics: 'grape', valuables: 'yellow', other: 'gray',
};

const EMPTY: Partial<ShelterProperty> = {
  ownerId: '', description: '', category: 'other', storageLocation: '', notes: '',
};

function QRCodeImage({ value, size = 140 }: { value: string; size?: number }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    QRCode.toDataURL(value, { width: size * 2, margin: 1 }).then(setSrc).catch(() => {});
  }, [value, size]);
  if (!src) return null;
  return <img src={src} alt={value} style={{ width: size, height: size }} />;
}

function generateQRCode(): string {
  // Simple unique ID for QR code tracking
  return `PROP-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
}

export default function Property() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    property, survivors, shelters,
    loadingProperty,
    loadProperty, loadSurvivors, loadShelters,
    createProperty, updateProperty, deleteProperty,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<ShelterProperty | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterProperty | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelterProperty | null>(null);
  const [form, setForm] = useState<Partial<ShelterProperty>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProperty(); loadSurvivors(); loadShelters(); }, [incidentId, loadProperty, loadSurvivors, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iProperty = incidentId ? property : [];
  const iSurvivors = incidentId ? survivors : [];
  const iShelters = incidentId ? shelters : [];

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    iShelters.forEach(s => m.set(String(s.Rid), s.name));
    return m;
  }, [iShelters]);

  // Lookup maps
  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    iSurvivors.forEach(s => m.set(String(s.Rid), `${s.firstName} ${s.lastName}`));
    return m;
  }, [iSurvivors]);

  const survivorOptions = useMemo(() =>
    iSurvivors.map(s => ({ value: String(s.Rid), label: `${s.firstName} ${s.lastName}` })),
    [iSurvivors],
  );

  // Find survivor's shelter
  const getSurvivorShelter = (survivorId: string): string => {
    const s = iSurvivors.find(sv => String(sv.Rid) === survivorId);
    return s?.shelterId ?? '';
  };

  const filtered = useMemo(() => {
    let list = iProperty;
    if (categoryFilter && categoryFilter !== 'all') list = list.filter(p => p.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.description.toLowerCase().includes(q) ||
        p.storageLocation.toLowerCase().includes(q) ||
        p.qrCode.toLowerCase().includes(q) ||
        (survivorMap.get(p.ownerId) ?? '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => new Date(b.checkedInAt).getTime() - new Date(a.checkedInAt).getTime());
  }, [iProperty, categoryFilter, search, survivorMap]);

  const setField = (key: keyof ShelterProperty, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (p: ShelterProperty) => { setEditTarget(p); setForm({ ...p }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.ownerId) {
      notifications.show({ message: 'Owner is required', color: 'yellow' });
      return;
    }
    if (!form.description?.trim()) {
      notifications.show({ message: 'Description is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const shelterId = getSurvivorShelter(form.ownerId ?? '');

      if (editTarget) {
        await updateProperty(editTarget.Rid, { ...form });
        notifications.show({ message: 'Property updated', color: 'green' });
      } else {
        const payload: Partial<ShelterProperty> = {
          ...form,
          incidentId,
          shelterId,
          qrCode: generateQRCode(),
          checkedInAt: new Date().toISOString(),
          checkedInBy: user?.fullName ?? user?.email ?? '',
        };
        await createProperty(payload);
        notifications.show({ message: 'Property checked in', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleCheckOut = async (p: ShelterProperty) => {
    setSaving(true);
    try {
      await updateProperty(p.Rid, { checkedOutAt: new Date().toISOString() });
      notifications.show({ message: `"${p.description}" checked out`, color: 'blue' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Check-out failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteProperty(deleteTarget.Rid);
      notifications.show({ message: `"${deleteTarget.description}" deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  // Print label sheet for all items belonging to one owner
  const handlePrintLabelSheet = async (ownerId: string) => {
    const ownerName = survivorMap.get(ownerId) || 'Unknown';
    const ownerSurvivor = iSurvivors.find(s => String(s.Rid) === ownerId);
    const ownerQr = ownerSurvivor?.qrCode ?? '';
    const ownerItems = iProperty
      .filter(p => p.ownerId === ownerId && !p.checkedOutAt)
      .map(p => ({ description: p.description, qrCode: p.qrCode, storageLocation: p.storageLocation }));
    await printPropertyLabelSheet(ownerName, ownerQr, ownerItems);
  };

  const handlePrintLabel = async (p: ShelterProperty) => {
    let qrImg = '';
    if (p.qrCode) {
      qrImg = await QRCode.toDataURL(p.qrCode, { width: 300, margin: 1 });
    }
    const ownerName = survivorMap.get(p.ownerId) || p.ownerId || 'Unknown';
    const checkInDate = p.checkedInAt ? new Date(p.checkedInAt).toLocaleDateString() : '';
    const w = window.open('', '_blank', 'width=400,height=500');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Property Tag</title><style>
      @page { size: 3in 2in; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; width: 3in; padding: 8px; }
      .header { text-align: center; font-size: 7px; font-weight: 700; color: var(--color-primary-800); text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid var(--color-primary-800); padding-bottom: 3px; margin-bottom: 5px; }
      .desc { text-align: center; font-size: 13px; font-weight: 700; margin-bottom: 4px; }
      .qr { text-align: center; margin: 4px 0; }
      .qr img { width: 120px; height: 120px; }
      .code { text-align: center; font-family: monospace; font-size: 8px; color: #555; margin-bottom: 5px; }
      .info { font-size: 8px; line-height: 1.5; }
      .info .label { font-weight: 700; color: #333; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    </style></head><body>
      <div class="header">OpenGov EMC &mdash; Property Tag</div>
      <div class="desc">${p.description || 'Item'}</div>
      <div class="qr">${qrImg ? `<img src="${qrImg}" />` : ''}</div>
      <div class="code">${p.qrCode || ''}</div>
      <div class="info">
        <span class="label">Owner:</span> ${ownerName}<br/>
        <span class="label">Storage:</span> ${p.storageLocation || 'N/A'}<br/>
        <span class="label">Checked In:</span> ${checkInDate}
      </div>
    </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Property & Belongings</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingProperty} onClick={() => loadProperty()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Check In Item</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput placeholder="Search description, owner, QR code..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={categoryFilter ?? 'all'} onChange={v => setCategoryFilter(v)} size="xs" w={170} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Owner</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Storage Location</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>QR Code</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Checked In</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Checked Out</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingProperty ? 'Loading...' : 'No property items found'}
                </td></tr>
              )}
              {filtered.map(p => (
                <tr key={p.id} onClick={() => setDetailTarget(p)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{p.description || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={CATEGORY_COLORS[p.category] ?? 'gray'}>
                      {p.category}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {(survivorMap.get(p.ownerId) ?? p.ownerId) || '-'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{p.storageLocation || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="xs" ff="monospace" c="dimmed">{p.qrCode || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="sm" c="dimmed">
                      {p.checkedInAt ? new Date(p.checkedInAt).toLocaleString() : '-'}
                    </Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {p.checkedOutAt ? (
                      <Text size="sm" c="dimmed">{new Date(p.checkedOutAt).toLocaleString()}</Text>
                    ) : (
                      <Badge size="xs" color="green">In Storage</Badge>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <Group gap={4} justify="flex-end">
                      {canEdit && !p.checkedOutAt && (
                        <ActionIcon size="sm" variant="subtle" color="blue" title="Check Out"
                          onClick={() => handleCheckOut(p)}>
                          <IconLogout size={14} />
                        </ActionIcon>
                      )}
                      <ActionIcon size="sm" variant="subtle" color="gray" title="Print Label"
                        onClick={() => handlePrintLabel(p)}>
                        <IconPrinter size={14} />
                      </ActionIcon>
                      {canEdit && (
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      )}
                      {canEdit && (
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(p)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Detail Drawer */}
      <Drawer opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        position="right" size="md" title="Property Details">
        {detailTarget && (
          <Stack>
            <Group>
              <Text size="lg" fw={600}>{detailTarget.description || '-'}</Text>
              <Badge size="lg" color={CATEGORY_COLORS[detailTarget.category] ?? 'gray'}>
                {detailTarget.category}
              </Badge>
            </Group>

            <Divider />

            <div>
              <Text size="xs" c="dimmed">Owner</Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>{survivorMap.get(detailTarget.ownerId) ?? detailTarget.ownerId ?? '-'}</Text>
                {detailTarget.ownerId && (
                  <Button size="compact-xs" variant="subtle"
                    onClick={() => { setDetailTarget(null); navigate('/shelter/survivor/' + detailTarget.ownerId); }}>
                    View Owner
                  </Button>
                )}
              </Group>
            </div>

            <div>
              <Text size="xs" c="dimmed">Shelter</Text>
              <Text size="sm">{shelterMap.get(detailTarget.shelterId) ?? detailTarget.shelterId ?? '-'}</Text>
            </div>

            <div>
              <Text size="xs" c="dimmed">Storage Location</Text>
              <Text size="sm">{detailTarget.storageLocation || '-'}</Text>
            </div>

            {detailTarget.qrCode && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <Text size="xs" c="dimmed" mb={4}>QR Code</Text>
                <QRCodeImage value={detailTarget.qrCode} size={140} />
                <Text size="xs" ff="monospace" c="dimmed" mt={4}>{detailTarget.qrCode}</Text>
              </div>
            )}

            <Divider />

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Checked In At</Text>
                <Text size="sm">{detailTarget.checkedInAt ? new Date(detailTarget.checkedInAt).toLocaleString() : '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Checked In By</Text>
                <Text size="sm">{detailTarget.checkedInBy || '-'}</Text>
              </div>
            </SimpleGrid>

            {detailTarget.checkedOutAt && (
              <div>
                <Text size="xs" c="dimmed">Checked Out At</Text>
                <Text size="sm">{new Date(detailTarget.checkedOutAt).toLocaleString()}</Text>
              </div>
            )}

            <div>
              <Text size="xs" c="dimmed">Notes</Text>
              <Text size="sm">{detailTarget.notes || '-'}</Text>
            </div>

            <Divider />

            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="light" color="teal" leftSection={<IconTags size={14} />}
                onClick={() => handlePrintLabelSheet(detailTarget.ownerId)}>
                Print All Owner Labels
              </Button>
              <Button size="xs" variant="light" color="gray" leftSection={<IconPrinter size={14} />}
                onClick={() => handlePrintLabel(detailTarget)}>
                Print Label
              </Button>
              {canEdit && (
                <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
                  onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>
                  Edit
                </Button>
              )}
              {canEdit && (
                <Button size="xs" variant="light" color="red" leftSection={<IconTrash size={14} />}
                  onClick={() => { setDetailTarget(null); setDeleteTarget(detailTarget); }}>
                  Delete
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Drawer>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Property' : 'Check In Property'} centered size="md">
        <Stack>
          <Select label="Owner (Survivor)" required searchable
            data={survivorOptions} value={form.ownerId ?? ''}
            onChange={v => setField('ownerId', v ?? '')}
            placeholder="Select survivor" />

          <TextInput label="Description" required value={form.description ?? ''}
            onChange={e => setField('description', e.target.value)}
            placeholder="e.g. Blue duffel bag, laptop, jewelry box" />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Category" data={CATEGORY_OPTIONS}
              value={form.category ?? 'other'}
              onChange={v => setField('category', v ?? 'other')} />
            <TextInput label="Storage Location" value={form.storageLocation ?? ''}
              onChange={e => setField('storageLocation', e.target.value)}
              placeholder="e.g. Room B, Shelf 3" />
          </SimpleGrid>

          {editTarget && form.qrCode && (
            <TextInput label="QR Code" readOnly value={form.qrCode}
              styles={{ input: { fontFamily: 'monospace', backgroundColor: 'var(--mantine-color-gray-1)' } }} />
          )}

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Check In Item'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Property" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.description}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
